import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, authAPI, teamsAPI } from '../services/api';
import { useApiState } from '../hooks/useApiState';
import './Settings.css';

function Settings() {
  const navigate = useNavigate();
  const { data: profile, loading, error, executeRequest } = useApiState(null);
  const [toast, setToast] = useState(null);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [notifyRoundReminders, setNotifyRoundReminders] = useState(true);
  const [notifyMatchReminders, setNotifyMatchReminders] = useState(true);
  const [favoriteTeamId, setFavoriteTeamId] = useState(null);

  // Teams data
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);

  // Loading states for each section
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingFavoriteTeam, setSavingFavoriteTeam] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [generatingResetToken, setGeneratingResetToken] = useState(false);

  useEffect(() => {
    loadProfile();
    loadTeams();
    markSettingsVisited();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSettingsVisited = async () => {
    try {
      await userAPI.markSettingsVisited();
    } catch (err) {
      // Silently fail - this is not critical
      console.log('Could not mark settings as visited:', err);
    }
  };

  const loadProfile = async () => {
    try {
      await executeRequest(async () => {
        const response = await userAPI.getProfile();
        const user = response.data.user;
        setDisplayName(user.display_name || '');
        setEmail(user.email || '');
        setNotifyRoundReminders(user.notify_round_reminders !== false);
        setNotifyMatchReminders(user.notify_match_reminders !== false);
        setFavoriteTeamId(user.favorite_team_id || null);
        return user;
      });
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await teamsAPI.getAll();
      setTeams(response.data.teams || []);
    } catch (err) {
      console.error('Failed to load teams:', err);
    } finally {
      setLoadingTeams(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSaveDisplayName = async () => {
    try {
      setSavingProfile(true);
      await userAPI.updateProfile({ displayName });
      await loadProfile();
      showToast('Display name updated successfully!');
    } catch (err) {
      console.error('Failed to update display name:', err);
      showToast(err.response?.data?.error || 'Failed to update display name', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveEmail = async () => {
    try {
      setSavingEmail(true);
      const response = await userAPI.updateProfile({ email });
      await loadProfile();

      if (response.data.emailVerificationSent) {
        showToast('Email updated! A verification link has been sent to your new email address.');
      } else {
        showToast('Email updated successfully!');
      }
    } catch (err) {
      console.error('Failed to update email:', err);
      showToast(err.response?.data?.error || 'Failed to update email', 'error');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSavingNotifications(true);
      await userAPI.updateProfile({
        notifyRoundReminders,
        notifyMatchReminders
      });
      await loadProfile();
      showToast('Notification preferences updated successfully!');
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      showToast(err.response?.data?.error || 'Failed to update notification preferences', 'error');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveFavoriteTeam = async () => {
    try {
      setSavingFavoriteTeam(true);
      await userAPI.updateProfile({ favoriteTeamId });
      await loadProfile();
      showToast('Favorite team updated successfully!');
    } catch (err) {
      console.error('Failed to update favorite team:', err);
      showToast(err.response?.data?.error || 'Failed to update favorite team', 'error');
    } finally {
      setSavingFavoriteTeam(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingVerification(true);
      await authAPI.verifyEmail('resend'); // We'll need to add this endpoint or use existing
      showToast('Verification email sent! Please check your inbox.');
    } catch (err) {
      console.error('Failed to resend verification:', err);
      showToast(err.response?.data?.error || 'Failed to send verification email', 'error');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setGeneratingResetToken(true);
      const response = await userAPI.generatePasswordResetToken();
      const { resetToken } = response.data;

      // Navigate to reset password page with token and selfService flag
      navigate(`/reset-password?token=${resetToken}&selfService=true`);
    } catch (err) {
      console.error('Failed to generate password reset token:', err);
      showToast(err.response?.data?.error || 'Failed to initiate password change', 'error');
    } finally {
      setGeneratingResetToken(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-container">
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button onClick={loadProfile} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="page-header">
        <button onClick={() => navigate('/picks')} className="btn-back">
          ← Back to Picks
        </button>
        <h1>Settings</h1>
        <div style={{ width: '120px' }}></div> {/* Spacer for alignment */}
      </div>

      {/* Display Name Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Display Name</h2>
          <p className="section-description">
            This is the name that will be shown in leaderboards and standings
          </p>
        </div>
        <div className="section-content">
          <div className="form-group">
            <label htmlFor="display-name">Display Name</label>
            <input
              id="display-name"
              type="text"
              className="form-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
            />
            <div className="form-hint">Leave blank to use your username: {profile?.username}</div>
          </div>
          <button
            onClick={handleSaveDisplayName}
            disabled={savingProfile}
            className="btn-save"
          >
            {savingProfile ? 'Saving...' : 'Save Display Name'}
          </button>
        </div>
      </div>

      {/* Favorite Team Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Favorite Team</h2>
          <p className="section-description">
            Select your favorite NRL team to personalize your experience
          </p>
        </div>
        <div className="section-content">
          <div className="form-group">
            <label htmlFor="favorite-team">Favorite Team</label>
            {loadingTeams ? (
              <div className="loading-teams">Loading teams...</div>
            ) : (
              <select
                id="favorite-team"
                className="team-select"
                value={favoriteTeamId || ''}
                onChange={(e) => setFavoriteTeamId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">No favorite team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.full_name}
                  </option>
                ))}
              </select>
            )}
            {favoriteTeamId && teams.length > 0 && (
              <div className="team-preview">
                {(() => {
                  const selectedTeam = teams.find(t => t.id === favoriteTeamId);
                  return selectedTeam ? (
                    <>
                      <img
                        src={`/assets/team-flags/${selectedTeam.flag_filename}`}
                        alt={selectedTeam.full_name}
                        className="team-flag-preview"
                      />
                      <span className="team-name-preview">{selectedTeam.full_name}</span>
                    </>
                  ) : null;
                })()}
              </div>
            )}
          </div>
          <button
            onClick={handleSaveFavoriteTeam}
            disabled={savingFavoriteTeam}
            className="btn-save"
          >
            {savingFavoriteTeam ? 'Saving...' : 'Save Favorite Team'}
          </button>
        </div>
      </div>

      {/* Email Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Email Address</h2>
          <p className="section-description">
            Your email is used for login and receiving notifications
          </p>
        </div>
        <div className="section-content">
          {!profile?.is_email_verified && (
            <div className="warning-banner">
              <span className="warning-icon">⚠️</span>
              <div className="warning-content">
                <strong>Email Not Verified</strong>
                <p>Please verify your email address to receive notifications and ensure account security.</p>
                <button
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                  className="btn-resend"
                >
                  {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
            />
            <div className="form-hint">
              Changing your email will require verification before you can receive notifications
            </div>
          </div>
          <button
            onClick={handleSaveEmail}
            disabled={savingEmail}
            className="btn-save"
          >
            {savingEmail ? 'Saving...' : 'Save Email'}
          </button>
        </div>
      </div>

      {/* Notification Preferences Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Email Notifications</h2>
          <p className="section-description">
            Choose which email reminders you want to receive
          </p>
        </div>
        <div className="section-content">
          <div className="notification-preferences">
            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Round Reminder Emails</div>
                <div className="preference-description">
                  Receive an email 24 hours before a round starts if you haven't completed all your picks
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={notifyRoundReminders}
                  onChange={(e) => setNotifyRoundReminders(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="preference-item">
              <div className="preference-info">
                <div className="preference-title">Match Reminder Emails</div>
                <div className="preference-description">
                  Receive an email 1 hour before a match starts if you haven't made a pick for that match
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={notifyMatchReminders}
                  onChange={(e) => setNotifyMatchReminders(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          <button
            onClick={handleSaveNotifications}
            disabled={savingNotifications}
            className="btn-save"
          >
            {savingNotifications ? 'Saving...' : 'Save Notification Preferences'}
          </button>

          <div className="notification-note">
            <strong>Note:</strong> You must have a verified email address to receive notifications.
            Admins can also disable notifications for all users for a specific season.
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Password & Security</h2>
          <p className="section-description">
            Manage your account password
          </p>
        </div>
        <div className="section-content">
          <div className="preference-item">
            <div className="preference-info">
              <div className="preference-title">Change Password</div>
              <div className="preference-description">
                Update your password to keep your account secure. You'll be redirected to a secure password reset page.
              </div>
            </div>
            <button
              onClick={handleChangePassword}
              disabled={generatingResetToken}
              className="btn-save"
            >
              {generatingResetToken ? 'Redirecting...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>

      {/* Account Info Section */}
      <div className="settings-section account-info">
        <div className="section-header">
          <h2>Account Information</h2>
        </div>
        <div className="section-content">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Username:</span>
              <span className="info-value">{profile?.username}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Role:</span>
              <span className="info-value role-badge">{profile?.role}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Member Since:</span>
              <span className="info-value">
                {new Date(profile?.created_at).toLocaleDateString('en-AU', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Total Picks:</span>
              <span className="info-value">{profile?.total_picks || 0}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Total Points:</span>
              <span className="info-value">{profile?.total_points || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default Settings;
